<template><div><h2 id="foreground" tabindex="-1" class="d-docsite--header-2">Foreground <a class="header-anchor d-link d-docsite--link d-link" href="#foreground" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Colors for text and icons</p>
<DesignColorTable :excluded-colors="textColorsExclusionList" class-prefix="d-fc-">
  <template #example="{color}">
    <div
      class="d-bar-circle d-w42 d-h42 d-ba d-bc-moderate d-bas-dashed"
      :style="`background-color: var(${color.tokenName})`"
    />
    <span
      :class="[
        'd-fl0 d-fs-300 d-p6 d-fw-medium',
        color.utilityClass,
        { 'd-bgc-contrast': color.utilityClass.endsWith('inverted') },
      ]"
    >
      Aa
    </span>
  </template>
</DesignColorTable>
<h2 id="foreground-status" tabindex="-1" class="d-docsite--header-2">Foreground Status <a class="header-anchor d-link d-docsite--link d-link" href="#foreground-status" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Foreground colors distinguishing important information with a status role.</p>
<DesignColorTable :excluded-colors="statusTextColorsExclusionList" class-prefix="d-fc-">
  <template #example="{color}">
    <div
      class="d-bar-circle d-w42 d-h42 d-ba d-bc-moderate d-bas-dashed"
      :style="`background-color: var(${color.tokenName})`"
    />
    <span
      :class="[
        'd-fl0 d-fs-300 d-p6 d-fw-medium',
        color.utilityClass,
        { 'd-bgc-contrast': color.utilityClass.endsWith('inverted') },
      ]"
    >
      Aa
    </span>
  </template>
</DesignColorTable>
<h2 id="surface" tabindex="-1" class="d-docsite--header-2">Surface <a class="header-anchor d-link d-docsite--link d-link" href="#surface" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Background colors for application UI surfaces. Surface colors are container blocks — such as pages, modals, tables, headers, and cards — containing foreground content or elements.</p>
<DesignColorTable :excluded-colors="surfaceColorsExclusionList" class-prefix="d-bgc-">
  <template #example="{color}">
    <div
      class="d-bar-circle d-w42 d-h42 d-ba d-bc-moderate d-bas-dashed"
      :style="`background: var(${color.tokenName})`"
    />
  </template>
</DesignColorTable>
<h2 id="borders" tabindex="-1" class="d-docsite--header-2">Borders <a class="header-anchor d-link d-docsite--link d-link" href="#borders" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Define the edge of key content area, components, or surfaces.</p>
<DesignColorTable class-prefix="d-bc-">
  <template #example="{color}">
    <div
      :class="[
        'd-d-inline-flex d-p4 d-bar-pill',
        { 'd-bgc-contrast': color.utilityClass.includes('inverted') },
      ]"
    >
      <div :class="`d-bar-circle d-w42 d-h42 d-ba d-bas-solid d-baw4 ${color.utilityClass}`"></div>
    </div>
  </template>
</DesignColorTable>
<h2 id="theme-shell" tabindex="-1" class="d-docsite--header-2">Theme Shell <a class="header-anchor d-link d-docsite--link d-link" href="#theme-shell" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">CSS variables for themeable parts of the UI, primarily targeting the top bar and left bar.</p>
<ThemeColorTable></ThemeColorTable><h2 id="base-palette" tabindex="-1" class="d-docsite--header-2">Base Palette <a class="header-anchor d-link d-docsite--link d-link" href="#base-palette" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Base colors are the literal value of all available colors. Use these only if all abstracted Text, Status text, Surface, or Border colors do not fit your need — consult a Product Designer.</p>
<p class="d-docsite--paragraph">Each of the colors listed above references these. For example, in Light mode
<code v-pre>var(--dt-color-foreground-primary)</code> is an alias to <code v-pre>var(--dt-color-black-900)</code>, and
<code v-pre>var(--dt-color-foreground-critical)</code> is an alias to
<code v-pre>var(--dt-color-red-300)</code>, and will have a different value in Dark mode.</p>
<h3 id="light-theme" tabindex="-1" class="d-docsite--header-3">Light Theme <a class="header-anchor d-link d-docsite--link d-link" href="#light-theme" aria-hidden="true">#</a></h3>
<ColorsCatalog mode="light"></ColorsCatalog><h3 id="dark-theme" tabindex="-1" class="d-docsite--header-3">Dark Theme <a class="header-anchor d-link d-docsite--link d-link" href="#dark-theme" aria-hidden="true">#</a></h3>
<ColorsCatalog mode="dark"></ColorsCatalog></div></template>


<script setup>
import DesignColorTable from '@baseComponents/DesignColorTable.vue';
import ThemeColorTable from '@baseComponents/ThemeColorTable.vue';
import ColorsCatalog from '@views/ColorsCatalog.vue';

/*
* Remove unwanted background-clip classes
*@TODO: Remove this once background-clip classes are refactored to d-bgclip https://dialpad.atlassian.net/browse/DLT-2439
*/
const surfaceColorsExclusionList = ['box', 'text'];
const textColorsExclusionList = ['critical',  'success',  'warning',  'info'];
const statusTextColorsExclusionList = [
  'current',
  'transparent',
  'unset',
  'neutral',
  'primary',
  'secondary',
  'tertiary',
  'placeholder',
  'disabled',
  'muted',
];
</script>